import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { DirectoryNumberResource } from '../resources/directory-number.resource';
import { ToastService } from '../../shared/services/toast.service';
import { ValidatorsService } from '../../shared/validators.service';
import { DnContainer, SelfServe360View, SelfServeUiContext } from '../contexts/self-serve-ui.context';
import { get } from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { DirectoryNumber, DirectoryNumberRef, EndUserResult } from '../../shared/models/generated/ziroModels';
import { Observable, Subscription, take } from 'rxjs';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../button/button.component';
import { ZiroFormAbstractDirective } from '../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../forms/ziro-form-state.service';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../forms/ziro-forms-models';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';
import { Global360ViewContext } from '../../shared/contexts/global-360-view.context';

interface SelfServeCallForward {
  callForward: string;
}

@Component({
  selector: 'ziro-self-serve-call-forward-edit',
  templateUrl: './self-serve-call-forward-edit.component.html',
  standalone: false,
})
export class SelfServeCallForwardEditComponent
  extends ZiroFormAbstractDirective<SelfServeCallForward>
  implements OnInit, OnDestroy
{
  private directoryNumberResource = inject(DirectoryNumberResource);
  private toastService = inject(ToastService);
  private selfServeUiContext = inject(SelfServeUiContext);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  protected _ziroFormStateService: ZiroFormStateService;
  private global360ViewContext = inject(Global360ViewContext);

  isLoading = true;
  directoryNumber: DirectoryNumber;
  user: EndUserResult;
  selfServe360View = {} as SelfServe360View;
  directoryNumberRef: DirectoryNumberRef;
  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;

  private _subscriptions = new Subscription();

  formConfig = {
    fields: {
      callForward: {
        label: 'tkey;selfserve.user.call_forward.label',
        dataAutomation: 'self-serve-call-forward-input',
        validation: [
          {
            validator: (value: string) => {
              return ValidatorsService.callForwardFormatValidationFn(value)
                ? ZiroFormsValidationState.VALID
                : ZiroFormsValidationState.INVALID;
            },
            message: this.translateService.instant('tkey;validators.global.error.pattern'),
          },
        ],
      },
    },
  } as ZiroFormConfig;

  ngOnInit(): void {
    this._initSelfServe();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  getDirectoryNumber: () => void = () => {
    const dnSub = this.directoryNumberResource
      .get(this.directoryNumberRef.id, this.directoryNumberRef.serverId.toString())
      .subscribe((data) => {
        this.directoryNumber = data;
        this.entitySource.next({
          callForward: data.forwardAll.destination,
        });
        this.isLoading = false;
      });
    this._subscriptions.add(dnSub);
  };

  private _onFormSubmit(): Observable<void> {
    return new Observable((subscriber) => {
      this.directoryNumber.forwardAll.destination = this.entity.callForward;
      this.directoryNumber.forwardAll.forwardToVoicemail = false;

      this.directoryNumberResource
        .put(this.directoryNumberRef.id, this.directoryNumber, this.directoryNumberRef.serverId.toString())
        .subscribe({
          next: () => {
            this.global360ViewContext.init(this.user.ref.username);
            this.selfServeUiContext.selfServe360View$
              .pipe(skip(1))
              .pipe(take(1))
              .subscribe(() => {
                this.toastService.pushSaveToast(
                  'tkey;selfserve.user.call_forward.label',
                  this.directoryNumber.forwardAll.destination,
                  this.ziroIcons.DESKPHONE_SMALL,
                );
                this.router.navigateByUrl(`self-serve/user/${encodeURIComponent(this.user.ref.username)}`);

                subscriber.next(null);
                subscriber.complete();
              });
          },
          error: () => {
            subscriber.next(null);
            subscriber.complete();
          },
        });
    });
  }

  private _initSelfServe: () => void = () => {
    if (!this.selfServeUiContext.selfServe360View) {
      const username = get(this.route, 'snapshot.params.username');
      this.selfServeUiContext.initUserDetails(username);
    }

    const subscription = this.selfServeUiContext.selfServe360View$
      .pipe(take(1))
      .subscribe((selfServe360View: SelfServe360View) => {
        if (selfServe360View) {
          const dnId = get(this.route, 'snapshot.params.dnId');

          this.user = selfServe360View.endUsers[0];
          this.selfServe360View = selfServe360View;
          const dnContainer = selfServe360View.dnContainers.find(
            (dnc: DnContainer) => dnc.directoryNumberRef.id === dnId,
          );

          if (!dnContainer) {
            const username = get(this.route, 'snapshot.params.username');
            this.router.navigateByUrl(`self-serve/user/${encodeURIComponent(username)}`);
            return;
          }

          this.directoryNumberRef = dnContainer.directoryNumberRef;
          this.getDirectoryNumber();
        }
      });
    this._subscriptions.add(subscription);
  };

  protected submit(): Observable<void> {
    return this._onFormSubmit();
  }
}
